import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { getUserIdentity } from "../utils/identity";
import "./PreferencesWizard.css";

const ALL_CATEGORIES = [
  "Electronics",
  "Fashion",
  "Home & Garden",
  "Gaming",
  "Vehicles",
  "Books",
  "Toys",
  "Sports Equipment",
  "Appliances",
  "Musical Instruments",
  "Collectibles",
  "Jewelry",
  "Tools",
  "Furniture",
  "Art",
  "Outdoor Gear",
];

const STYLE_OPTIONS = [
  "Vintage",
  "Modern",
  "Contemporary",
  "Luxury",
  "Bohemian",
  "Minimalist",
  "Industrial",
  "Rustic",
  "Scandinavian",
  "Eclectic",
  "Urban",
  "Classic",
];

const CATEGORY_ICONS = {
  Electronics: "💻",
  Fashion: "👗",
  "Home & Garden": "🏡",
  Gaming: "🎮",
  Vehicles: "🚗",
  Books: "📚",
  Toys: "🧸",
  "Sports Equipment": "🏅",
  Appliances: "🔌",
  "Musical Instruments": "🎵",
  Collectibles: "⭐",
  Jewelry: "💍",
  Tools: "🛠️",
  Furniture: "🛋️",
  Art: "🖼️",
  "Outdoor Gear": "⛺",
};

const DISTANCES = ["25", "50", "100", "250", "500", "1000"];

const STEPS = [
  { key: "basics", title: "Interests", subtitle: "Choose the categories and styles you want to see more often." },
  { key: "budget", title: "Budget", subtitle: "Set your ideal price range and how far you want to browse." },
  { key: "location", title: "Location", subtitle: "Help us prioritise listings around you." },
  { key: "weights", title: "Priority", subtitle: "Tell us how you prefer your feed to behave." },
  { key: "review", title: "Review", subtitle: "Confirm your setup and save." },
];

const PRESETS = [
  {
    key: "starter",
    title: "Quick Start",
    subtitle: "A simple everyday setup for browsing",
    apply: (set) => {
      set.categories(["Electronics", "Home & Garden", "Fashion"]);
      set.styles(["Modern", "Minimalist"]);
      set.condition("Any");
      set.preferredRadius("100");
      set.budgetMin("");
      set.budgetMax("");
      set.wantsUnique(false);
      set.weights({
        category: 4,
        style: 2,
        condition: 2,
        tradeType: 3,
        location: 3,
      });
      set.matchMode("balanced");
    },
  },
  {
    key: "deals",
    title: "Best Deals",
    subtitle: "Budget-led and nearby-first recommendations",
    apply: (set) => {
      set.categories(["Electronics", "Appliances", "Furniture"]);
      set.styles([]);
      set.condition("Used");
      set.preferredRadius("50");
      set.budgetMin("");
      set.budgetMax("5000");
      set.wantsUnique(false);
      set.weights({
        category: 3,
        style: 1,
        condition: 4,
        tradeType: 3,
        location: 5,
      });
      set.matchMode("budget_first");
    },
  },
  {
    key: "premium",
    title: "Premium Taste",
    subtitle: "Higher-value and more polished listings first",
    apply: (set) => {
      set.categories(["Fashion", "Jewelry", "Art", "Furniture"]);
      set.styles(["Luxury", "Classic", "Contemporary"]);
      set.condition("Any");
      set.preferredRadius("250");
      set.budgetMin("1000");
      set.budgetMax("");
      set.wantsUnique(true);
      set.weights({
        category: 4,
        style: 4,
        condition: 2,
        tradeType: 3,
        location: 2,
      });
      set.matchMode("premium_finds");
    },
  },
  {
    key: "collectors",
    title: "Rare Finds",
    subtitle: "Unique, collectible, and harder-to-find items",
    apply: (set) => {
      set.categories(["Collectibles", "Art", "Books", "Jewelry"]);
      set.styles(["Vintage", "Classic"]);
      set.condition("Any");
      set.preferredRadius("500");
      set.budgetMin("");
      set.budgetMax("");
      set.wantsUnique(true);
      set.weights({
        category: 5,
        style: 4,
        condition: 2,
        tradeType: 3,
        location: 2,
      });
      set.matchMode("interest_first");
    },
  },
];

function clampWeight(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 3;
  return Math.max(1, Math.min(5, num));
}

export default function PreferencesWizard({
  mode = "page",
  open = true,
  onClose = () => {},
  onSaved = () => {},
  onAfterSaveNavigate = null,
}) {
  const { currentUser } = useAuth();
  const identity = useMemo(() => getUserIdentity(currentUser), [currentUser]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const [categories, setCategories] = useState([]);
  const [styles, setStyles] = useState([]);
  const [condition, setCondition] = useState("Any");
  const [, setTradeType] = useState(["Cash"]);
  const [preferredRadius, setPreferredRadius] = useState("100");

  const [autoCountry, setAutoCountry] = useState("");
  const [autoCity, setAutoCity] = useState("");
  const [autoLanguage, setAutoLanguage] = useState("English");
  const [autoCurrency, setAutoCurrency] = useState("ZAR");
  const [autoLat, setAutoLat] = useState(null);
  const [autoLng, setAutoLng] = useState(null);

  const [manualLocation, setManualLocation] = useState({ country: "", city: "" });
  const [geoFailed, setGeoFailed] = useState(false);

  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [wantsUnique, setWantsUnique] = useState(false);

  const [weights, setWeights] = useState({
    category: 3,
    style: 2,
    condition: 2,
    tradeType: 3,
    location: 3,
  });

  const [useAISuggestions, setUseAISuggestions] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [matchMode, setMatchMode] = useState("balanced");

  const presetApi = useMemo(
    () => ({
      categories: (v) => setCategories(v),
      styles: (v) => setStyles(v),
      condition: (v) => setCondition(v),
      preferredRadius: (v) => setPreferredRadius(v),
      budgetMin: (v) => setBudgetMin(v),
      budgetMax: (v) => setBudgetMax(v),
      wantsUnique: (v) => setWantsUnique(v),
      weights: (v) =>
        setWeights({
          category: clampWeight(v?.category),
          style: clampWeight(v?.style),
          condition: clampWeight(v?.condition),
          tradeType: clampWeight(v?.tradeType),
          location: clampWeight(v?.location),
        }),
      matchMode: (v) => setMatchMode(v),
    }),
    []
  );

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (!identity.id) {
        setLoading(false);
        return;
      }

      try {
        const prefRef = db.collection("preferences").doc(identity.id);
        const aiRef = db.collection("aiSuggestions").doc(identity.id);

        const [prefSnap, aiSnap] = await Promise.all([prefRef.get(), aiRef.get()]);

        if (prefSnap.exists) {
          const d = prefSnap.data() || {};

          setCategories(Array.isArray(d.favoriteCategories) ? d.favoriteCategories : []);
          setStyles(Array.isArray(d.preferredStyles) ? d.preferredStyles : []);
          setCondition(d.preferredCondition || "Any");
          setTradeType(["Cash"]);
          setBudgetMin(d.budgetMin ?? "");
          setBudgetMax(d.budgetMax ?? "");
          setWantsUnique(!!d.wantsUnique);
          setPreferredRadius(d.preferredRadius?.toString?.() || "100");

          const incomingWeights = d.matchingWeights || {};
          setWeights({
            category: clampWeight(incomingWeights.category ?? 3),
            style: clampWeight(incomingWeights.style ?? 2),
            condition: clampWeight(incomingWeights.condition ?? 2),
            tradeType: clampWeight(incomingWeights.tradeType ?? 3),
            location: clampWeight(incomingWeights.location ?? 3),
          });

          const loc = d.location || {};
          setAutoCountry(loc.country || "South Africa");
          setAutoCity((Array.isArray(loc.cities) && loc.cities[0]) || "");
          setAutoLanguage(loc.language || "English");
          setAutoCurrency(loc.currency || "ZAR");
          setAutoLat(loc.latitude ?? null);
          setAutoLng(loc.longitude ?? null);
          setManualLocation({
            country: loc.country || "",
            city: (Array.isArray(loc.cities) && loc.cities[0]) || "",
          });
        } else {
          setAutoCountry("South Africa");
          setAutoCurrency("ZAR");
          setAutoLanguage("English");
          setTradeType(["Cash"]);
        }

        if (aiSnap.exists) {
          const ai = aiSnap.data() || {};
          const aiPrefs = ai.suggestedPreferences || {};
          const aiWeights = ai.recommendedWeights || null;
          const forceApply = !!ai.forceApply;

          if (!prefSnap.exists || forceApply || useAISuggestions) {
            setCategories(Array.isArray(aiPrefs.favoriteCategories) ? aiPrefs.favoriteCategories : []);
            setStyles(Array.isArray(aiPrefs.preferredStyles) ? aiPrefs.preferredStyles : []);
            setCondition(aiPrefs.preferredCondition || "Any");
            setTradeType(["Cash"]);

            if (aiWeights) {
              setWeights({
                category: clampWeight(aiWeights.category ?? 3),
                style: clampWeight(aiWeights.style ?? 2),
                condition: clampWeight(aiWeights.condition ?? 2),
                tradeType: clampWeight(aiWeights.tradeType ?? 3),
                location: clampWeight(aiWeights.location ?? 3),
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to load preferences:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [identity.id, useAISuggestions]);

  useEffect(() => {
    const isLocationStep = STEPS[stepIdx]?.key === "location";
    if (!isLocationStep) return;

    let cancelled = false;

    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          if (cancelled) return;

          setAutoLat(coords.latitude);
          setAutoLng(coords.longitude);

          try {
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`
            );
            const data = await res.json();

            if (cancelled) return;

            const detectedCountry = data.countryName || "South Africa";
            const detectedCity = data.city || data.locality || "";

            setAutoCountry(detectedCountry);
            setAutoCity(detectedCity);
            setAutoLanguage(navigator.language || "English");
            setAutoCurrency(data.currency || (data.countryCode === "ZA" ? "ZAR" : "USD"));
            setGeoFailed(false);

            if (!manualLocation.country && !manualLocation.city) {
              setManualLocation({
                country: detectedCountry,
                city: detectedCity,
              });
            }
          } catch (error) {
            console.warn("Reverse geocode failed:", error);
            setGeoFailed(true);
          }
        },
        (error) => {
          console.warn("Geolocation error:", error);
          setGeoFailed(true);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGeoFailed(true);
    }

    return () => {
      cancelled = true;
    };
  }, [stepIdx]);

  const budgetValidation = useMemo(() => {
    const min =
      budgetMin === "" || budgetMin == null ? null : Number(budgetMin);
    const max =
      budgetMax === "" || budgetMax == null ? null : Number(budgetMax);

    const hasMin = min !== null && Number.isFinite(min);
    const hasMax = max !== null && Number.isFinite(max);

    if (hasMin && min < 0) {
      return "Minimum budget cannot be negative.";
    }

    if (hasMax && max < 0) {
      return "Maximum budget cannot be negative.";
    }

    if (hasMin && hasMax && min > max) {
      return "Minimum budget cannot be higher than maximum budget.";
    }

    return "";
  }, [budgetMin, budgetMax]);

  const canProceed = useMemo(() => {
    const key = STEPS[stepIdx]?.key;

    switch (key) {
      case "basics":
        return categories.length > 0;
      case "budget":
        return !budgetValidation;
      case "location": {
        const city = manualLocation.city || autoCity;
        const country = manualLocation.country || autoCountry;
        return !!country && !!city;
      }
      case "weights":
      case "review":
      default:
        return true;
    }
  }, [stepIdx, categories, manualLocation, autoCity, autoCountry, budgetValidation]);

  const completionScore = useMemo(() => {
    let score = 0;
    if (categories.length > 0) score += 30;
    if (styles.length > 0) score += 10;
    if (condition) score += 10;
    if (budgetMin !== "" || budgetMax !== "") score += 15;
    if (preferredRadius) score += 10;
    if ((manualLocation.country || autoCountry) && (manualLocation.city || autoCity)) score += 15;
    if (wantsUnique) score += 5;
    if (useAISuggestions) score += 5;
    return Math.min(score, 100);
  }, [
    categories,
    styles,
    condition,
    budgetMin,
    budgetMax,
    preferredRadius,
    manualLocation,
    autoCountry,
    autoCity,
    wantsUnique,
    useAISuggestions,
  ]);

  const preview = useMemo(() => {
    const topCats = categories.slice(0, 3);
    const topStyles = styles.slice(0, 3);
    const weightTop = Object.entries(weights)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 2)
      .map(([k]) => k);

    return {
      highlight: weightTop.length ? weightTop : ["category", "location"],
      cats: topCats.length ? topCats.join(", ") : "Not selected yet",
      styles: topStyles.length ? topStyles.join(", ") : "No style preference",
      condition,
      tradeType: "Cash",
      budget: `${budgetMin ? `From R${budgetMin}` : "No minimum"} · ${budgetMax ? `Up to R${budgetMax}` : "No maximum"}`,
      location: `${manualLocation.city || autoCity || "Your city"}, ${manualLocation.country || autoCountry || "Your country"}`,
      radius: `${preferredRadius} km`,
      unique: wantsUnique ? "Yes" : "No",
    };
  }, [
    categories,
    styles,
    condition,
    weights,
    budgetMin,
    budgetMax,
    manualLocation,
    autoCity,
    autoCountry,
    preferredRadius,
    wantsUnique,
  ]);

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const prev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const showToast = (message) => {
    setToastMsg(message);
    window.setTimeout(() => setToastMsg(""), 1800);
  };

  const handleCheckboxGroup = (value, setFn, max = null) => (e) => {
    const { checked } = e.target;
    setFn((prev) => {
      if (!checked) return prev.filter((v) => v !== value);
      if (max && prev.length >= max) return prev;
      return [...new Set([...prev, value])];
    });
  };

  const clearBasics = () => {
    setCategories([]);
    setStyles([]);
    setCondition("Any");
  };

  const applyPreset = (key) => {
    const preset = PRESETS.find((p) => p.key === key);
    if (!preset) return;
    preset.apply(presetApi);
    setTradeType(["Cash"]);
    showToast(`Applied preset: ${preset.title}`);
  };

  const applyMatchMode = (mode) => {
    setMatchMode(mode);

    if (mode === "balanced") {
      setWantsUnique(false);
      setWeights({
        category: 4,
        style: 2,
        condition: 2,
        tradeType: 3,
        location: 3,
      });
      return;
    }

    if (mode === "budget_first") {
      setWantsUnique(false);
      setWeights({
        category: 3,
        style: 1,
        condition: 3,
        tradeType: 3,
        location: 2,
      });
      return;
    }

    if (mode === "nearby_first") {
      setWantsUnique(false);
      setWeights({
        category: 3,
        style: 1,
        condition: 2,
        tradeType: 3,
        location: 5,
      });
      return;
    }

    if (mode === "interest_first") {
      setWantsUnique(false);
      setWeights({
        category: 5,
        style: 3,
        condition: 2,
        tradeType: 3,
        location: 2,
      });
      return;
    }

    if (mode === "premium_finds") {
      setWantsUnique(true);
      setWeights({
        category: 4,
        style: 4,
        condition: 2,
        tradeType: 3,
        location: 2,
      });
    }
  };

  const handleSave = async () => {
    if (!identity.id) return;

    if (budgetValidation) {
      alert(budgetValidation);
      return;
    }

    const resolvedCountry = (manualLocation.country || autoCountry || "").trim();
    const resolvedCity = (manualLocation.city || autoCity || "").trim();

    const locationPayload = {
      country: resolvedCountry,
      cities: resolvedCity ? [resolvedCity] : [],
      latitude: autoLat ?? null,
      longitude: autoLng ?? null,
      language: autoLanguage,
      currency: autoCurrency,
    };

    if (!locationPayload.country || locationPayload.cities.length === 0) {
      alert("Please enter your city and country before saving.");
      return;
    }

    setSaving(true);

    try {
      const prefRef = db.collection("preferences").doc(identity.id);
      const userRef = db.collection("users").doc(identity.id);

      await prefRef.set(
        {
          favoriteCategories: categories,
          preferredStyles: styles,
          preferredCondition: condition,
          tradeType: ["Cash"],
          preferredRadius: Number(preferredRadius) || 100,
          location: locationPayload,
          budgetMin: budgetMin === "" ? null : Number(budgetMin),
          budgetMax: budgetMax === "" ? null : Number(budgetMax),
          tradeFrequency: "Cash buyer",
          wantsUnique,
          preferencesCompleted: true,
          matchingWeights: weights,
          identity: {
            provider: identity.provider,
            email: identity.email ?? null,
            phone: identity.phone ?? null,
            label: identity.label,
          },
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      await userRef.set({ preferencesSet: true }, { merge: true });

      onSaved?.();
      showToast("Preferences saved");

      if (typeof onAfterSaveNavigate === "function") {
        onAfterSaveNavigate("/market/recommended");
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      alert("Could not save preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (mode === "modal" && !open) return null;

  if (loading) {
    return (
      <div className="pref-container">
        <div className="pref-loading-card">
          <div className="pref-loading-spinner" />
          <p>Loading your setup…</p>
        </div>
      </div>
    );
  }

  const stepKey = STEPS[stepIdx]?.key;

  return (
    <div className={mode === "modal" ? "pref-modal-overlay" : "pref-page"}>
      <div className={mode === "modal" ? "pref-modal-shell" : "pref-shell"}>
        <div className="pref-header">
          <div className="pref-header-copy">
            <div className="pref-kicker">Personalise your marketplace</div>
            <h2>Personalise Your Shopping Preferences</h2>
            <p className="subtitle">{STEPS[stepIdx].subtitle}</p>
          </div>

          <div className="user-pill">{identity.label}</div>

          {mode === "modal" && (
            <button className="icon-btn" onClick={onClose} aria-label="Close" type="button">
              ✕
            </button>
          )}
        </div>

        {toastMsg ? <div className="pref-toast">{toastMsg}</div> : null}

        <Progress
          value={((stepIdx + 1) / STEPS.length) * 100}
          steps={STEPS}
          activeIndex={stepIdx}
        />

        <div className="pref-main">
          <div className="pref-stage">
            {stepKey === "basics" && (
              <>
                <section className="hero-panel">
                  <div className="hero-panel__copy">
                    <span className="eyebrow">Smart setup</span>
                    <h3>Tell us what you want to see more of</h3>
                    <p>
                      Select what you actually care about and Nexter Zone will push better
                      cash listings, better styles, and more relevant nearby items higher up.
                    </p>
                  </div>
                </section>

                <section className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>Quick setup</h4>
                      <p>Choose a preset, then refine it.</p>
                    </div>
                  </div>

                  <div className="preset-grid">
                    {PRESETS.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        className="preset-card"
                        onClick={() => applyPreset(preset.key)}
                      >
                        <div className="preset-name">{preset.title}</div>
                        <div className="preset-sub">{preset.subtitle}</div>
                      </button>
                    ))}
                  </div>
                </section>

               <section className="stacked-preference-panels">
                  <div className="content-section preference-panel-wide">
                    <div className="section-top">
                      <div>
                        <h4>Favourite Categories</h4>
                        <p>Choose up to 6 categories you want to see more often.</p>
                      </div>
                      <div className="section-tools">
                        <span className="count-badge">{categories.length} selected</span>
                        <button type="button" className="mini-link" onClick={() => setCategories([])}>
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="chip-grid category-grid category-grid-wide">
                      {ALL_CATEGORIES.map((category) => (
                        <label
                          key={category}
                          className={`select-chip ${categories.includes(category) ? "active" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={categories.includes(category)}
                            onChange={handleCheckboxGroup(category, setCategories, 6)}
                          />
                          <span className="chip-icon">{CATEGORY_ICONS[category] || "•"}</span>
                          <span>{category}</span>
                        </label>
                      ))}
                    </div>

                    <div className="field condition-field-compact">
                      <label>Preferred Condition</label>
                      <select value={condition} onChange={(e) => setCondition(e.target.value)}>
                        <option value="Any">Any</option>
                        <option value="New">New</option>
                        <option value="Used">Used</option>
                      </select>
                    </div>
                  </div>

                  <div className="content-section preference-panel-wide">
                    <div className="section-top">
                      <div>
                        <h4>Preferred Styles</h4>
                        <p>Optional, but useful for fashion, décor, furniture, art, and collectibles.</p>
                      </div>
                      <div className="section-tools">
                        <span className="count-badge">{styles.length} selected</span>
                        <button type="button" className="mini-link" onClick={() => setStyles([])}>
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="chip-grid styles-grid-wide">
                      {STYLE_OPTIONS.map((style) => (
                        <label
                          key={style}
                          className={`select-chip ${styles.includes(style) ? "active" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={styles.includes(style)}
                            onChange={handleCheckboxGroup(style, setStyles, 5)}
                          />
                          <span>{style}</span>
                        </label>
                      ))}
                    </div>

                    <div className="info-card compact-info-card">
                      <div className="info-card__title">Tip</div>
                      <div className="info-card__text">
                        A few strong categories with 1 to 3 styles usually gives the cleanest recommendations.
                      </div>
                    </div>

                    <div className="actions-inline">
                      <button type="button" className="btn ghost" onClick={clearBasics}>
                        Reset selections
                      </button>
                    </div>
                  </div>
                </section>

                <section className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>Your feed preview</h4>
                      <p>See how your selections will affect recommendations.</p>
                    </div>
                  </div>

                  <div className="sidebar-card sidebar-card--inline">
                    <div className="sidebar-card__title">Your recommendations will focus on</div>

                    <div className="pill-row">
                      {preview.highlight.map((key) => (
                        <span key={key} className="pill">
                          {key === "tradeType" ? "Cash listings" : formatKey(key)}
                        </span>
                      ))}
                    </div>

                    <div className="pill-row subtle-row">
                      <span className="pill subtle">Cash only</span>
                      <span className="pill subtle">{preview.radius}</span>
                    </div>

                    <div className="sidebar-list">
                      <div><span>Categories</span><b>{preview.cats}</b></div>
                      <div><span>Styles</span><b>{preview.styles}</b></div>
                      <div><span>Condition</span><b>{preview.condition}</b></div>
                      <div><span>Budget</span><b>{preview.budget}</b></div>
                      <div><span>Location</span><b>{preview.location}</b></div>
                    </div>
                  </div>
                </section>
              </>
            )}

            {stepKey === "budget" && (
              <section className="two-col">
                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>Budget and distance</h4>
                      <p>Tell us the price range and distance that suit you best.</p>
                    </div>
                  </div>

                  <div className="field-row">
                    <div className={`field ${budgetValidation ? "field-error" : ""}`}>
                      <label>Minimum budget (ZAR)</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={budgetMin}
                        onChange={(e) => setBudgetMin(e.target.value)}
                        placeholder="e.g. 500"
                      />
                    </div>

                    <div className={`field ${budgetValidation ? "field-error" : ""}`}>
                      <label>Maximum budget (ZAR)</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={budgetMax}
                        onChange={(e) => setBudgetMax(e.target.value)}
                        placeholder="e.g. 5000"
                      />
                    </div>
                  </div>

                  {budgetValidation ? (
                    <div className="budget-warning">
                      {budgetValidation}
                    </div>
                  ) : null}

                  <div className="field">
                    <label>Search radius</label>
                    <select
                      value={preferredRadius}
                      onChange={(e) => setPreferredRadius(e.target.value)}
                    >
                      {DISTANCES.map((km) => (
                        <option key={km} value={km}>
                          {km} km
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="info-card">
                    <div className="info-card__title">Why this helps</div>
                    <div className="info-card__text">
                      Wider ranges give more variety. Narrower ranges give tighter, more targeted results.
                    </div>
                  </div>
                </div>

                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>How your feed will adapt</h4>
                      <p>Your selections shape ranking, not what you are allowed to browse.</p>
                    </div>
                  </div>

                  <div className="bullet-card">
                    <ul className="review-list">
                      <li>Items in your budget range can appear earlier.</li>
                      <li>Nearby listings can be prioritised for easier buying.</li>
                      <li>Your selected categories and styles influence recommendations.</li>
                      <li>You can still browse everything outside your saved preferences.</li>
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {stepKey === "location" && (
              <section className="two-col">
                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>Detected location</h4>
                      <p>We use this to show better local opportunities first.</p>
                    </div>
                  </div>

                  <div className="location-card">
                    <div className="location-card__label">Detected</div>

                    {!geoFailed ? (
                      <>
                        <div className="location-card__place">
                          {autoCity || "City not found"}, {autoCountry || "Country not found"}
                        </div>
                        <div className="location-card__meta">
                          Language: {autoLanguage || "—"} · Currency: {autoCurrency || "—"}
                        </div>

                        <button
                          type="button"
                          className="btn ghost"
                          onClick={() =>
                            setManualLocation({
                              country: autoCountry || "",
                              city: autoCity || "",
                            })
                          }
                        >
                          Use detected location
                        </button>
                      </>
                    ) : (
                      <div className="location-card__fallback">
                        We could not detect your location automatically. Please enter it manually.
                      </div>
                    )}
                  </div>
                </div>

                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>Confirm your location</h4>
                      <p>This is the value that will be saved and used in matching.</p>
                    </div>
                  </div>

                  <div className="field">
                    <label>Your country</label>
                    <input
                      type="text"
                      value={manualLocation.country}
                      onChange={(e) =>
                        setManualLocation((prev) => ({ ...prev, country: e.target.value }))
                      }
                      placeholder="Enter country"
                    />
                  </div>

                  <div className="field">
                    <label>Your city</label>
                    <input
                      type="text"
                      value={manualLocation.city}
                      onChange={(e) =>
                        setManualLocation((prev) => ({ ...prev, city: e.target.value }))
                      }
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="info-card">
                    <div className="info-card__title">Important</div>
                    <div className="info-card__text">
                      Your city is essential. It improves nearby recommendations and fixes the issue where city was not being picked up properly.
                    </div>
                  </div>
                </div>
              </section>
            )}

            {stepKey === "weights" && (
              <section className="two-col">
                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>What should we prioritise first?</h4>
                      <p>Pick one mode. This is much friendlier than raw priority sliders.</p>
                    </div>
                  </div>

                  <div className="mode-grid">
                    {[
                      {
                        key: "balanced",
                        title: "Balanced",
                        subtitle: "A healthy mix of relevance, price, and distance",
                        icon: "⚖️",
                      },
                      {
                        key: "budget_first",
                        title: "Best value",
                        subtitle: "Focus more on budget-friendly options",
                        icon: "💸",
                      },
                      {
                        key: "nearby_first",
                        title: "Closest to me",
                        subtitle: "Prioritise listings around your area",
                        icon: "📍",
                      },
                      {
                        key: "interest_first",
                        title: "Best match",
                        subtitle: "Push the categories and styles you like most",
                        icon: "🎯",
                      },
                      {
                        key: "premium_finds",
                        title: "Standout finds",
                        subtitle: "Surface more polished and premium-looking listings",
                        icon: "✨",
                      },
                    ].map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={`mode-card ${matchMode === item.key ? "active" : ""}`}
                        onClick={() => applyMatchMode(item.key)}
                      >
                        <div className="mode-card__icon">{item.icon}</div>
                        <div className="mode-card__title">{item.title}</div>
                        <div className="mode-card__subtitle">{item.subtitle}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="content-section">
                  <div className="section-top">
                    <div>
                      <h4>AI Suggestions</h4>
                      <p>Use your AI profile to prefill the setup, then adjust it manually.</p>
                    </div>
                  </div>

                  <div className="ai-card">
                    <div className="ai-row">
                      <div>
                        <div className="ai-title">Enable AI Suggestions</div>
                        <div className="ai-subtitle">
                          This can prefill categories, styles, and recommendation weightings.
                        </div>
                      </div>

                      <label className="ai-toggle" aria-label="Toggle AI suggestions">
                        <input
                          type="checkbox"
                          checked={useAISuggestions}
                          onChange={(e) => setUseAISuggestions(e.target.checked)}
                        />
                        <span className="track">
                          <span className="thumb" />
                        </span>
                      </label>
                    </div>

                    <div className="ai-state-pill">
                      {useAISuggestions ? "AI is on" : "AI is off"}
                    </div>

                    <div className="preview-mini">
                      <div><b>Categories:</b> {preview.cats}</div>
                      <div><b>Styles:</b> {preview.styles}</div>
                      <div><b>Condition:</b> {preview.condition}</div>
                      <div><b>Location:</b> {preview.location}</div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {stepKey === "review" && (
              <section className="content-section">
                <div className="section-top">
                  <div>
                    <h4>Review your setup</h4>
                    <p>This is what will be saved to personalise your marketplace experience.</p>
                  </div>
                </div>

                <div className="success-banner">
                  <div className="success-banner__title">Ready to save</div>
                  <div className="success-banner__text">
                    Your homepage, recommendations, and ranking will update after saving.
                  </div>
                </div>

                <div className="review-grid">
                  <div>
                    <b>Categories</b>
                    <p>{categories.join(", ") || "—"}</p>
                  </div>
                  <div>
                    <b>Styles</b>
                    <p>{styles.join(", ") || "—"}</p>
                  </div>
                  <div>
                    <b>Condition</b>
                    <p>{condition}</p>
                  </div>
                  <div>
                    <b>Listing Type</b>
                    <p>Cash only</p>
                  </div>
                  <div>
                    <b>Budget</b>
                    <p>{preview.budget}</p>
                  </div>
                  <div>
                    <b>Radius</b>
                    <p>{preferredRadius} km</p>
                  </div>
                  <div>
                    <b>Location</b>
                    <p>{manualLocation.city || autoCity || "—"}, {manualLocation.country || autoCountry || "—"}</p>
                  </div>
                  <div>
                    <b>Priority mode</b>
                    <p>{labelForMatchMode(matchMode)}</p>
                  </div>
                  <div>
                    <b>Wants unique items</b>
                    <p>{wantsUnique ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <b>AI Suggestions</b>
                    <p>{useAISuggestions ? "Enabled" : "Disabled"}</p>
                  </div>
                </div>
              </section>
            )}
          </div>

          <aside className="pref-sidebar">
            <div className="sidebar-card sidebar-card--accent">
              <div className="sidebar-card__label">Feed preview</div>
              <div className="sidebar-card__title">Your recommendations will focus on</div>

              <div className="pill-row">
                {preview.highlight.map((key) => (
                  <span key={key} className="pill">
                    {key === "tradeType" ? "Cash listings" : formatKey(key)}
                  </span>
                ))}
              </div>

              <div className="pill-row subtle-row">
                <span className="pill subtle">Cash only</span>
                <span className="pill subtle">{preview.radius}</span>
              </div>

              <div className="sidebar-list">
                <div><span>Categories</span><b>{preview.cats}</b></div>
                <div><span>Styles</span><b>{preview.styles}</b></div>
                <div><span>Condition</span><b>{preview.condition}</b></div>
                <div><span>Budget</span><b>{preview.budget}</b></div>
                <div><span>Location</span><b>{preview.location}</b></div>
              </div>
            </div>

            <div className="sidebar-card">
              <div className="sidebar-card__label">Progress</div>
              <div className="completion-ring-wrap">
                <div className="completion-ring">
                  <span>{completionScore}%</span>
                </div>
                <div>
                  <div className="sidebar-card__title">Setup completeness</div>
                  <p className="sidebar-copy">
                    A more complete profile gives cleaner recommendations.
                  </p>
                </div>
              </div>
            </div>

            <div className="sidebar-card">
              <div className="sidebar-card__label">How it works</div>
              <ul className="sidebar-bullets">
                <li>Your preferences affect ranking, not access.</li>
                <li>You can still browse any listing in the marketplace.</li>
                <li>You can change this setup later anytime.</li>
              </ul>
            </div>
          </aside>
        </div>

        <div className="pref-footer">
          <div className="pref-footer__left">
            <StepDots total={STEPS.length} active={stepIdx} onClick={setStepIdx} />
          </div>

          <div className="pref-footer__right">
            {stepIdx > 0 && (
              <button className="btn ghost" onClick={prev} type="button">
                Back
              </button>
            )}

            {stepIdx < STEPS.length - 1 && (
              <button className="btn primary" disabled={!canProceed} onClick={next} type="button">
                Next
              </button>
            )}

            {stepIdx === STEPS.length - 1 && (
              <button className="btn primary" onClick={handleSave} disabled={saving} type="button">
                {saving ? "Saving…" : "Save & Start Browsing"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function labelForMatchMode(mode) {
  if (mode === "budget_first") return "Best value";
  if (mode === "nearby_first") return "Closest to me";
  if (mode === "interest_first") return "Best match";
  if (mode === "premium_finds") return "Standout finds";
  return "Balanced";
}

function formatKey(key) {
  if (key === "tradeType") return "Cash listings";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function Progress({ value, steps, activeIndex }) {
  return (
    <div className="progress-wrap">
      <div className="progress-line">
        <div className="progress-line__bar" style={{ width: `${value}%` }} />
      </div>

      <div className="milestones">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`milestone ${index <= activeIndex ? "active" : ""} ${index === activeIndex ? "current" : ""}`}
          >
            <span className="milestone__dot" />
            <span className="milestone__label">{step.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepDots({ total, active, onClick }) {
  return (
    <div className="dots">
      {Array.from({ length: total }).map((_, index) => (
        <button
          key={index}
          className={`dot-btn ${index === active ? "active" : ""}`}
          onClick={() => onClick(index)}
          aria-label={`Go to step ${index + 1}`}
          type="button"
        />
      ))}
    </div>
  );
}